import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Card, CardContent, IconButton, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BarChartIcon from "@mui/icons-material/BarChart";
import { getMyMoods, getMoodSummary } from "../services/api";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const FULL_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const moodLabel = (score) => {
  if (score >= 4.5) return "Excellent";
  if (score >= 3.5) return "Good";
  if (score >= 2.5) return "Okay";
  if (score >= 1.5) return "Low";
  return "Very Low";
};

const moodColor = (score) => {
  if (score >= 4.5) return "#4ade80";
  if (score >= 3.5) return "#22d3ee";
  if (score >= 2.5) return "#f59e0b";
  if (score >= 1.5) return "#f97316";
  return "#ef4444";
};

const moodEmoji = (score) => {
  if (score >= 4.5) return "😄";
  if (score >= 3.5) return "🙂";
  if (score >= 2.5) return "😐";
  if (score >= 1.5) return "😔";
  return "😞";
};

export default function MoodInsights() {
  const navigate = useNavigate();
  const [moods, setMoods] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

 useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function loadData() {
        try {
            const [moodsRes, summaryRes] = await Promise.all([
                getMyMoods(),
                getMoodSummary(),
            ]);
            setMoods(moodsRes.data);
            setSummary(summaryRes.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                navigate("/");
            }
        }
        setLoading(false);
    }

  // Day of week averages — uses mood.score (matches the API)
  const dayAverages = DAYS.map((_, dayIndex) => {
    const dayMoods = moods.filter(
      (m) => new Date(m.created_at).getDay() === dayIndex
    );
    if (dayMoods.length === 0) return null;
    const avg = dayMoods.reduce((sum, m) => sum + m.score, 0) / dayMoods.length;
    return { avg, count: dayMoods.length };
  });

  const validDays = dayAverages
    .map((d, i) => (d ? { ...d, day: i } : null))
    .filter(Boolean);

  const bestDay = validDays.length
    ? validDays.reduce((a, b) => (a.avg > b.avg ? a : b))
    : null;
  const worstDay = validDays.length
    ? validDays.reduce((a, b) => (a.avg < b.avg ? a : b))
    : null;

  // Last 7 entries trend
  const last7 = moods
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 7)
    .reverse();

  const trendDirection = () => {
    if (last7.length < 2) return "flat";
    const half = Math.ceil(last7.length / 2);
    const first = last7.slice(0, half);
    const second = last7.slice(half);
    const avgFirst = first.reduce((s, m) => s + m.score, 0) / first.length;
    const avgSecond = second.reduce((s, m) => s + m.score, 0) / second.length;
    if (avgSecond - avgFirst > 0.3) return "up";
    if (avgFirst - avgSecond > 0.3) return "down";
    return "flat";
  };

  // Score distribution
  const distribution = [1, 2, 3, 4, 5].map((score) => ({
    score,
    count: moods.filter((m) => m.score === score).length,
  }));
  const maxDist = Math.max(...distribution.map((d) => d.count), 1);

  const trend = trendDirection();
  const maxDayAvg = Math.max(...validDays.map((d) => d.avg), 5);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", background: "#0f0f1a" }}>
      <CircularProgress sx={{ color: "#4ade80" }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(135deg, #0a1628, #0d1f3c, #0a2040)", px: { xs: 2, sm: 3 }, py: 4, pb: 6 }}>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: "#7fa8c8", mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ color: "#e8f4f8", fontWeight: 700, fontSize: "1.3rem" }}>
          Mood Insights
        </Typography>
      </Box>

      {moods.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography sx={{ fontSize: "3rem", mb: 2 }}>📊</Typography>
          <Typography sx={{ color: "#7fa8c8", fontSize: "1rem" }}>
            No mood data yet. Log a few moods and come back!
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Overview */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Card sx={cardStyle}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography sx={{ color: "#7fa8c8", fontSize: "0.72rem", letterSpacing: "0.08em", mb: 0.5 }}>
                  TOTAL LOGGED
                </Typography>
                <Typography sx={{ color: "#4ade80", fontWeight: 900, fontSize: "2rem" }}>
                  {moods.length}
                </Typography>
                <Typography sx={{ color: "#3a5a7a", fontSize: "0.72rem" }}>entries</Typography>
              </CardContent>
            </Card>
            <Card sx={cardStyle}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography sx={{ color: "#7fa8c8", fontSize: "0.72rem", letterSpacing: "0.08em", mb: 0.5 }}>
                  OVERALL AVG
                </Typography>
                <Typography sx={{ color: summary ? moodColor(summary.average_score) : "#22d3ee", fontWeight: 900, fontSize: "2rem" }}>
                  {summary ? summary.average_score.toFixed(1) : "-"}
                </Typography>
                <Typography sx={{ color: "#3a5a7a", fontSize: "0.72rem" }}>
                  {summary ? moodLabel(summary.average_score) : ""}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Trend */}
          <Card sx={cardStyle}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: "#22d3ee", fontSize: 20 }} />
                <Typography sx={{ color: "#7fa8c8", fontSize: "0.75rem", letterSpacing: "0.08em", fontWeight: 700 }}>
                  RECENT TREND (LAST {last7.length} ENTRIES)
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 60, mb: 1.5 }}>
                {last7.map((m, i) => (
                  <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{
                      width: "100%",
                      height: `${(m.score / 5) * 52}px`,
                      background: moodColor(m.score),
                      borderRadius: "4px 4px 0 0",
                      opacity: 0.85,
                    }} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                {last7.map((m, i) => (
                  <Box key={i} sx={{ flex: 1, textAlign: "center" }}>
                    <Typography sx={{ color: "#3a5a7a", fontSize: "0.6rem" }}>
                      {new Date(m.created_at).toLocaleDateString("en-KE", { weekday: "short" })}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{
                display: "flex", alignItems: "center", gap: 1,
                background: trend === "up" ? "rgba(74,222,128,0.1)" : trend === "down" ? "rgba(239,68,68,0.1)" : "rgba(34,211,238,0.1)",
                borderRadius: 2, px: 2, py: 1,
              }}>
                {trend === "up" && <TrendingUpIcon sx={{ color: "#4ade80", fontSize: 20 }} />}
                {trend === "down" && <TrendingDownIcon sx={{ color: "#ef4444", fontSize: 20 }} />}
                {trend === "flat" && <TrendingFlatIcon sx={{ color: "#22d3ee", fontSize: 20 }} />}
                <Typography sx={{
                  color: trend === "up" ? "#4ade80" : trend === "down" ? "#ef4444" : "#22d3ee",
                  fontSize: "0.82rem", fontWeight: 600,
                }}>
                  {trend === "up" && "Your mood has been improving lately 🎉"}
                  {trend === "down" && "Your mood has dipped recently — be kind to yourself 🌿"}
                  {trend === "flat" && "Your mood has been steady recently"}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Best & Worst Day */}
          {bestDay && worstDay && bestDay.day !== worstDay.day && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Card sx={{ ...cardStyle, border: "1px solid rgba(74,222,128,0.25)" }}>
                <CardContent sx={{ py: 2, textAlign: "center" }}>
                  <Typography sx={{ color: "#7fa8c8", fontSize: "0.7rem", letterSpacing: "0.08em", mb: 1 }}>BEST DAY</Typography>
                  <Typography sx={{ fontSize: "1.8rem", mb: 0.5 }}>{moodEmoji(bestDay.avg)}</Typography>
                  <Typography sx={{ color: "#4ade80", fontWeight: 800, fontSize: "1rem" }}>{FULL_DAYS[bestDay.day]}</Typography>
                  <Typography sx={{ color: "#3a5a7a", fontSize: "0.72rem" }}>avg {bestDay.avg.toFixed(1)} / 5</Typography>
                </CardContent>
              </Card>
              <Card sx={{ ...cardStyle, border: "1px solid rgba(239,68,68,0.2)" }}>
                <CardContent sx={{ py: 2, textAlign: "center" }}>
                  <Typography sx={{ color: "#7fa8c8", fontSize: "0.7rem", letterSpacing: "0.08em", mb: 1 }}>TOUGHEST DAY</Typography>
                  <Typography sx={{ fontSize: "1.8rem", mb: 0.5 }}>{moodEmoji(worstDay.avg)}</Typography>
                  <Typography sx={{ color: "#f97316", fontWeight: 800, fontSize: "1rem" }}>{FULL_DAYS[worstDay.day]}</Typography>
                  <Typography sx={{ color: "#3a5a7a", fontSize: "0.72rem" }}>avg {worstDay.avg.toFixed(1)} / 5</Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Day of Week Chart */}
          {validDays.length > 1 && (
            <Card sx={cardStyle}>
              <CardContent sx={{ py: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                  <CalendarTodayIcon sx={{ color: "#22d3ee", fontSize: 20 }} />
                  <Typography sx={{ color: "#7fa8c8", fontSize: "0.75rem", letterSpacing: "0.08em", fontWeight: 700 }}>
                    AVERAGE MOOD BY DAY
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 80 }}>
                  {DAYS.map((day, i) => {
                    const d = dayAverages[i];
                    return (
                      <Box key={day} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        {d ? (
                          <Box sx={{
                            width: "100%",
                            height: `${(d.avg / maxDayAvg) * 68}px`,
                            background: moodColor(d.avg),
                            borderRadius: "4px 4px 0 0",
                            opacity: bestDay?.day === i ? 1 : 0.55,
                          }} />
                        ) : (
                          <Box sx={{ width: "100%", height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 1 }} />
                        )}
                        <Typography sx={{ color: bestDay?.day === i ? "#4ade80" : "#3a5a7a", fontSize: "0.65rem", fontWeight: bestDay?.day === i ? 700 : 400 }}>
                          {day}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Distribution */}
          <Card sx={cardStyle}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                <BarChartIcon sx={{ color: "#22d3ee", fontSize: 20 }} />
                <Typography sx={{ color: "#7fa8c8", fontSize: "0.75rem", letterSpacing: "0.08em", fontWeight: 700 }}>
                  MOOD DISTRIBUTION
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {[5, 4, 3, 2, 1].map((score) => {
                  const d = distribution.find((x) => x.score === score);
                  const pct = d ? (d.count / maxDist) * 100 : 0;
                  return (
                    <Box key={score} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography sx={{ fontSize: "1rem", width: 24 }}>{moodEmoji(score)}</Typography>
                      <Box sx={{ flex: 1, height: 10, background: "rgba(255,255,255,0.05)", borderRadius: 5, overflow: "hidden" }}>
                        <Box sx={{
                          height: "100%",
                          width: `${pct}%`,
                          background: moodColor(score),
                          borderRadius: 5,
                          opacity: 0.85,
                        }} />
                      </Box>
                      <Typography sx={{ color: "#7fa8c8", fontSize: "0.75rem", width: 20, textAlign: "right" }}>
                        {d?.count || 0}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>

          {/* Insight callout */}
          {bestDay && worstDay && (
            <Card sx={{ ...cardStyle, border: "1px solid rgba(74,222,128,0.2)" }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <EmojiEmotionsIcon sx={{ color: "#4ade80", fontSize: 20, mt: 0.2 }} />
                  <Box>
                    <Typography sx={{ color: "#4ade80", fontWeight: 700, fontSize: "0.82rem", mb: 0.5 }}>
                      Imara Insight
                    </Typography>
                    <Typography sx={{ color: "#7fa8c8", fontSize: "0.82rem", lineHeight: 1.6 }}>
                      {bestDay.day === worstDay.day
                        ? `You've only logged moods on ${FULL_DAYS[bestDay.day]}s so far. Keep logging daily for better insights!`
                        : `You tend to feel best on ${FULL_DAYS[bestDay.day]}s and toughest on ${FULL_DAYS[worstDay.day]}s. On ${FULL_DAYS[worstDay.day]}s, try a breathing exercise or check the devotional early.`
                      }
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

        </Box>
      )}
    </Box>
  );
}

const cardStyle = {
  background: "rgba(13, 31, 60, 0.85)",
  border: "1px solid rgba(255,255,255,0.07)",
  borderRadius: 3,
};